import {
  AlertTriangle,
  Skull,
  Globe,
  Users,
  DollarSign,
  Shield,
  ExternalLink,
  TrendingDown,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  API,
  COLORS,
  FOUNDER_LABELS,
  MONETIZATION_LABELS,
  GOVERNANCE_LABELS,
  CHART,
} from "../../lib/constants";
import { usePrefetchOrFetch } from "../common/DataPrefetch";

const TOOLTIP_STYLE = CHART.tooltip;

const EXTRACTION_COLOR = "#ef4444";
const ACCENT = "#f97316";

export const ExtractionAnatomy = () => {
  const [data, loading] = usePrefetchOrFetch(
    "extractionAnatomy",
    `${API}/extraction-anatomy`,
    {
      validate: (d) => d && typeof d.total_extraction === "number" && d.countries && d.comparison,
      delay: 500,
    }
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="spinner mr-3" />
        <span className="text-muted-foreground">Analyzing extraction AI...</span>
      </div>
    );
  }

  if (!data) return null;

  const {
    total_extraction,
    total_global,
    pct_of_global,
    countries,
    monetization,
    founders,
    governance,
    sources,
    notable_apps,
    comparison,
  } = data;

  // Chart data
  const countryData = countries.map((c) => ({
    name: c.country,
    count: c.count,
  }));

  const monetizationData = Object.entries(monetization).map(([k, v]) => ({
    name: MONETIZATION_LABELS[k] || k,
    value: v,
    key: k,
  }));
  monetizationData.sort((a, b) => b.value - a.value);

  const founderData = Object.entries(founders).map(([k, v]) => ({
    name: FOUNDER_LABELS[k] || k,
    value: v,
    key: k,
  }));
  founderData.sort((a, b) => b.value - a.value);

  const governanceData = Object.entries(governance).map(([k, v]) => ({
    name: GOVERNANCE_LABELS[k] || k,
    value: v,
    key: k,
  }));
  governanceData.sort((a, b) => b.value - a.value);

  const founderTotal = founderData.reduce((s, d) => s + d.value, 0);
  const govTotal = governanceData.reduce((s, d) => s + d.value, 0);

  // Comparison bars data
  const comparisonData = [
    {
      label: "Female-Founded",
      extraction: comparison.ext_female_pct,
      global: comparison.global_female_pct,
    },
    {
      label: "Enterprise Model",
      extraction: comparison.ext_enterprise_pct,
      global: comparison.global_enterprise_pct,
    },
  ];

  const PIE_COLORS = {
    male_only: COLORS.male_only,
    female_only: COLORS.female_only,
    mixed: COLORS.mixed,
    market_first: COLORS.market_first,
    neutral: COLORS.neutral,
    system_aware: COLORS.system_aware,
    participatory: "#06b6d4",
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="extraction-anatomy">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Skull className="w-6 h-6 text-red-500" />
          Anatomy of Extraction AI
        </h2>
        <p className="text-muted-foreground mt-1 text-sm">
          Deep-dive into the {pct_of_global}% of AI applications classified as
          extraction-oriented — products that prioritize data monetization,
          attention capture, or aggressive growth over user wellbeing.
        </p>
      </div>

      {/* Hero stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <HeroStat
          label="Extraction Apps"
          value={total_extraction.toLocaleString()}
          sub={`out of ${total_global.toLocaleString()} total`}
          icon={AlertTriangle}
          color={EXTRACTION_COLOR}
        />
        <HeroStat
          label="Share of Global"
          value={`${pct_of_global}%`}
          sub="of all catalogued AI"
          icon={TrendingDown}
          color={ACCENT}
        />
        <HeroStat
          label="Market-First Governance"
          value={`${comparison.ext_market_first_pct}%`}
          sub="adopt growth-dominant messaging"
          icon={Shield}
          color="#f59e0b"
        />
        <HeroStat
          label="Female-Founded"
          value={`${comparison.ext_female_pct}%`}
          sub={`vs ${comparison.global_female_pct}% globally`}
          icon={Users}
          color={COLORS.female_only}
        />
      </div>

      {/* Extraction vs Global Comparison */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="font-semibold mb-1 text-base">
          Extraction AI vs Global Average
        </h3>
        <p className="text-xs text-muted-foreground mb-5">
          How extraction-oriented products differ from the broader AI ecosystem
        </p>
        <div className="space-y-5">
          {comparisonData.map((c) => (
            <ComparisonBar key={c.label} {...c} />
          ))}
          {/* Market-first single bar */}
          <div>
            <div className="flex items-center justify-between text-sm mb-1.5">
              <span className="text-muted-foreground">Market-First Governance</span>
              <span className="font-semibold text-amber-400">
                {comparison.ext_market_first_pct}%
              </span>
            </div>
            <div className="h-3 bg-muted/30 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{
                  width: `${Math.min(comparison.ext_market_first_pct, 100)}%`,
                  background: "linear-gradient(90deg, #f59e0b, #ef4444)",
                }}
              />
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">
              Nearly two-thirds of extraction apps adopt growth-dominant messaging
            </p>
          </div>
        </div>
      </div>

      {/* Two-column layout: Countries + Monetization */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Geographic Spread */}
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="font-semibold mb-1 text-base flex items-center gap-2">
            <Globe className="w-4 h-4 text-muted-foreground" />
            Geographic Spread
          </h3>
          <p className="text-xs text-muted-foreground mb-4">
            Top countries producing extraction-oriented AI
          </p>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={countryData}
                layout="vertical"
                margin={{ left: 10, right: 20, top: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={CHART.gridStroke} horizontal={false} />
                <XAxis type="number" tick={CHART.axis} stroke={CHART.axisStroke} />
                <YAxis
                  dataKey="name"
                  type="category"
                  width={85}
                  tick={CHART.axis}
                  stroke={CHART.axisStroke}
                  interval={0}
                />
                <Tooltip {...TOOLTIP_STYLE} formatter={(v) => [v, "Apps"]} />
                <Bar dataKey="count" radius={CHART.barRadius} maxBarSize={20}>
                  {countryData.map((_, i) => (
                    <Cell
                      key={i}
                      fill={i === 0 ? EXTRACTION_COLOR : `${EXTRACTION_COLOR}${Math.max(30, 90 - i * 5).toString(16)}`}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Monetization */}
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="font-semibold mb-1 text-base flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-muted-foreground" />
            Monetization Models
          </h3>
          <p className="text-xs text-muted-foreground mb-4">
            How extraction AI generates revenue
          </p>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={monetizationData}
                layout="vertical"
                margin={{ left: 10, right: 20, top: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={CHART.gridStroke} horizontal={false} />
                <XAxis type="number" tick={CHART.axis} stroke={CHART.axisStroke} />
                <YAxis
                  dataKey="name"
                  type="category"
                  width={100}
                  tick={CHART.axis}
                  stroke={CHART.axisStroke}
                  interval={0}
                />
                <Tooltip {...TOOLTIP_STYLE} formatter={(v) => [v, "Apps"]} />
                <Bar dataKey="value" radius={CHART.barRadius} maxBarSize={20} fill={ACCENT} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Two-column: Founders + Governance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Founder Composition Donut */}
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="font-semibold mb-1 text-base flex items-center gap-2">
            <Users className="w-4 h-4 text-muted-foreground" />
            Founder Composition
          </h3>
          <p className="text-xs text-muted-foreground mb-4">
            Gender makeup of extraction AI founding teams (known only)
          </p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={founderData}
                  cx="50%"
                  cy="50%"
                  innerRadius={CHART.pieInner}
                  outerRadius={CHART.pieOuter}
                  dataKey="value"
                  paddingAngle={2}
                >
                  {founderData.map((d) => (
                    <Cell key={d.key} fill={PIE_COLORS[d.key] || "#6b7280"} />
                  ))}
                </Pie>
                <Tooltip
                  {...TOOLTIP_STYLE}
                  formatter={(v) => [v, "Apps"]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2">
            {founderData.map((d) => (
              <div key={d.key} className="flex items-center gap-1.5 text-xs">
                <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: PIE_COLORS[d.key] || "#6b7280" }} />
                <span className="text-muted-foreground">{d.name}</span>
                <span className="font-semibold">{((d.value / founderTotal) * 100).toFixed(1)}%</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-center text-muted-foreground mt-2">
            Female-founded: {comparison.ext_female_pct}% (global avg: {comparison.global_female_pct}%)
          </p>
        </div>

        {/* Governance Framing Donut */}
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="font-semibold mb-1 text-base flex items-center gap-2">
            <Shield className="w-4 h-4 text-muted-foreground" />
            Governance Framing
          </h3>
          <p className="text-xs text-muted-foreground mb-4">
            How extraction AI positions itself on ethics and safety
          </p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={governanceData}
                  cx="50%"
                  cy="50%"
                  innerRadius={CHART.pieInner}
                  outerRadius={CHART.pieOuter}
                  dataKey="value"
                  paddingAngle={2}
                >
                  {governanceData.map((d) => (
                    <Cell key={d.key} fill={PIE_COLORS[d.key] || "#6b7280"} />
                  ))}
                </Pie>
                <Tooltip
                  {...TOOLTIP_STYLE}
                  formatter={(v) => [v, "Apps"]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2">
            {governanceData.map((d) => (
              <div key={d.key} className="flex items-center gap-1.5 text-xs">
                <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: PIE_COLORS[d.key] || "#6b7280" }} />
                <span className="text-muted-foreground">{d.name}</span>
                <span className="font-semibold">{((d.value / govTotal) * 100).toFixed(1)}%</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-center text-muted-foreground mt-2">
            {comparison.ext_market_first_pct}% adopt market-first governance framing
          </p>
        </div>
      </div>

      {/* Notable Extraction Apps Table */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="font-semibold mb-1 text-base">
          Notable Extraction-Oriented Apps
        </h3>
        <p className="text-xs text-muted-foreground mb-4">
          Sample of {total_extraction} extraction-classified applications
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="extraction-apps-table">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="text-left p-3 font-medium">App Name</th>
                <th className="text-left p-3 font-medium">Country</th>
                <th className="text-left p-3 font-medium">Monetization</th>
                <th className="text-left p-3 font-medium">Founders</th>
                <th className="text-left p-3 font-medium">Source</th>
                <th className="text-left p-3 font-medium w-10"></th>
              </tr>
            </thead>
            <tbody>
              {notable_apps.slice(0, 20).map((app, idx) => (
                <tr
                  key={idx}
                  className={`border-b border-border/50 hover:bg-muted/30 transition-colors ${idx % 2 === 0 ? "" : "bg-muted/10"}`}
                >
                  <td className="p-3">
                    <p className="font-medium truncate max-w-[200px]">{app.product_name}</p>
                    <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                      {app.tagline?.substring(0, 60)}
                    </p>
                  </td>
                  <td className="p-3 text-muted-foreground">{app.country || "—"}</td>
                  <td className="p-3">
                    <span className="px-2 py-0.5 rounded-full text-xs bg-muted/60 text-muted-foreground">
                      {MONETIZATION_LABELS[app.monetization_model] || app.monetization_model}
                    </span>
                  </td>
                  <td className="p-3">
                    <span className="px-2 py-0.5 rounded-full text-xs bg-muted/60 text-muted-foreground">
                      {FOUNDER_LABELS[app.founder_composition] || app.founder_composition}
                    </span>
                  </td>
                  <td className="p-3 text-xs text-muted-foreground">{formatSource(app.source)}</td>
                  <td className="p-3">
                    {app.website_url && (
                      <a
                        href={app.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 hover:bg-muted rounded-lg inline-block"
                      >
                        <ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {notable_apps.length > 20 && (
          <p className="text-xs text-muted-foreground mt-3 text-center">
            Showing 20 of {notable_apps.length} extraction apps
          </p>
        )}
      </div>

      {/* Key Takeaway */}
      <div className="bg-red-500/5 border border-red-500/15 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-sm text-red-300 mb-1">Key Takeaway</h4>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Extraction-oriented AI represents only {pct_of_global}% of the global ecosystem,
              but it is disproportionately concentrated in the USA ({countries[0]?.count || 0} of {total_extraction} apps),
              overwhelmingly market-first in governance ({comparison.ext_market_first_pct}%), and
              has nearly half the female founder representation ({comparison.ext_female_pct}%) compared to
              the global average ({comparison.global_female_pct}%).
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Sub-components ── */

const HeroStat = ({ label, value, sub, icon: Icon, color }) => (
  <div className="bg-card border border-border rounded-xl p-5 card-hover" data-testid={`extraction-stat-${label.replace(/\s/g, "-").toLowerCase()}`}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-muted-foreground text-xs font-medium mb-1">{label}</p>
        <p className="text-2xl font-bold" style={{ color }}>
          {value}
        </p>
        <p className="text-muted-foreground text-xs mt-1">{sub}</p>
      </div>
      <div className="p-2 rounded-lg" style={{ backgroundColor: color + "15" }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
    </div>
  </div>
);

const ComparisonBar = ({ label, extraction, global }) => {
  const max = Math.max(extraction, global, 1);
  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1.5">
        <span className="text-muted-foreground">{label}</span>
      </div>
      <div className="space-y-1.5">
        <div className="flex items-center gap-3">
          <span className="text-[10px] w-16 text-right text-red-400 font-medium">Extraction</span>
          <div className="flex-1 h-3 bg-muted/30 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full bg-red-500 transition-all duration-700"
              style={{ width: `${(extraction / max) * 100}%` }}
            />
          </div>
          <span className="text-xs font-semibold w-12 text-right text-red-400">{extraction}%</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] w-16 text-right text-blue-400 font-medium">Global</span>
          <div className="flex-1 h-3 bg-muted/30 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full bg-blue-500 transition-all duration-700"
              style={{ width: `${(global / max) * 100}%` }}
            />
          </div>
          <span className="text-xs font-semibold w-12 text-right text-blue-400">{global}%</span>
        </div>
      </div>
    </div>
  );
};

function formatSource(src) {
  if (!src) return "—";
  if (src.includes("taaft")) return "TAAFT";
  if (src.includes("huggingface")) return "HuggingFace";
  if (src.toLowerCase().includes("google") || src.includes("gplay")) return "Google Play";
  if (src.includes("futurepedia")) return "Futurepedia";
  if (src.includes("product_hunt")) return "Product Hunt";
  return src.replace(/_/g, " ");
}
